/**
 * Recharts chart theme — mirrors the fleetGUARDIAN DevExpress chart values
 * (`dxChartTheme.ts`) so charts read as the same product without adopting the
 * MUI-coupled dx-react-chart lib. Import these into every Recharts chart:
 * the palette, semantic fills, axis/grid props, the shared tooltip, and the
 * 600ms animation duration.
 */
import type { CSSProperties } from 'react';

/** Series palette — index order is load-bearing (assigned by series index). */
export const CHART_PALETTE = [
  '#1F6B75',
  '#3A8F9B',
  '#5BB0B5',
  '#7DCACC', // teal ramp (brand anchor)
  '#2563eb',
  '#10b981',
  '#f59e0b', // petrol · diesel · oil
  '#ef4444',
  '#8b5cf6',
  '#06b6d4', // high/blocked · EV · toll
  '#ec4899',
  '#14b8a6', // pink · toll-spend
] as const;

/** Named semantic fills — prefer these over palette indices where meaning matters. */
export const SEMANTIC_FILLS = {
  petrol: '#2563eb',
  diesel: '#10b981',
  oil: '#f59e0b',
  toll: '#14b8a6',
  severityHigh: '#ef4444',
  severityMedium: '#f59e0b',
  severityLow: '#10b981',
  operational: '#2563eb',
  active: '#10b981',
  dormant: '#f59e0b',
  disposed: '#ef4444',
  ev: '#06b6d4',
} as const;

export const CHART_GRID_STROKE = '#f3f4f6';
export const CHART_TICK_FILL = '#6b7280';
export const CHART_LABEL_FILL = '#374151';
export const CHART_ANIMATION_MS = 600;

/** Spread onto `<XAxis/>` / `<YAxis/>`: 12px gray ticks, no axis/tick lines. */
export const chartAxis = {
  tick: { fill: CHART_TICK_FILL, fontSize: 12 },
  axisLine: false as const,
  tickLine: false as const,
};

/** Spread onto `<CartesianGrid/>`: faint horizontal gridlines only. */
export const chartGrid = {
  stroke: CHART_GRID_STROKE,
  vertical: false as const,
};

/** Legend props: circle icons, 12px. */
export const chartLegend = {
  iconType: 'circle' as const,
  wrapperStyle: { fontSize: 12, paddingTop: 10 },
};

/**
 * Tooltip content style — white / 1px #e5e7eb / radius 8 / soft shadow / 12px /
 * tabular-nums. Spread onto Recharts `<Tooltip contentStyle={chartTooltipStyle}/>`.
 */
export const chartTooltipStyle: CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  boxShadow:
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  fontSize: 12,
  padding: '8px 12px',
  fontVariantNumeric: 'tabular-nums',
  color: '#111827',
};

export const chartTooltipItemStyle: CSSProperties = {
  color: '#111827',
  fontVariantNumeric: 'tabular-nums',
};

export const chartTooltipLabelStyle: CSSProperties = {
  color: '#6b7280',
  marginBottom: 4,
};

/** Resolve a series colour by index (wraps the palette). */
export function seriesColor(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length];
}
